import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    MenuItem,
    Box,
    Typography,
    CircularProgress,
    FormControlLabel,
    Checkbox,
    FormGroup,
    FormLabel,
    Collapse,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Paper
} from "@mui/material";
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import axios from "axios";
import { APIURL } from "../../config";

export default function CustomReportBuilder(props) {
    const { open, onClose, user, report, availableBaseReports, setSuccessText, setErrorText } = props;
    
    const [reportName, setReportName] = useState("");
    const [baseReport, setBaseReport] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const [availableColumns, setAvailableColumns] = useState([]);
    const [columnVisibility, setColumnVisibility] = useState({});
    const [isLoadingColumns, setIsLoadingColumns] = useState(false);
    const [columnOrder, setColumnOrder] = useState([]);
    const [draggedIndex, setDraggedIndex] = useState(null);

    useEffect(() => {
        if (report) {
            // Editing existing report
            setReportName(report.report_name || "");
            setBaseReport(report.base_report || "");
            
            // Parse configuration if it's a string
            let parsedConfig = report.configuration;
            if (typeof parsedConfig === 'string') {
                try {
                    parsedConfig = JSON.parse(parsedConfig);
                } catch (e) {
                    parsedConfig = {};
                }
            }
            
            // Load existing column visibility if available
            if (parsedConfig?.columnVisibilityModel) {
                setColumnVisibility(parsedConfig.columnVisibilityModel);
            }
            
            // Load existing column order if available
            if (parsedConfig?.columns?.orderedFields) {
                setColumnOrder(parsedConfig.columns.orderedFields);
            }
            
            // Fetch columns for the base report when editing
            if (report.base_report) {
                fetchColumnsForReport(report.base_report, parsedConfig);
            }
        } else {
            // Creating new report
            setReportName("");
            setBaseReport("");
            setAvailableColumns([]);
            setColumnVisibility({});
            setColumnOrder([]);
        }
    }, [report]);

    // Fetch available columns when base report changes (for new reports)
    useEffect(() => {
        if (baseReport && !report) {
            fetchColumnsForReport(baseReport);
        }
    }, [baseReport, report]);

    const fetchColumnsForReport = async (reportName, existingConfig = null) => {
        try {
            setIsLoadingColumns(true);
            // Fetch a sample of data from the base report to get column names
            const response = await axios.get(`${APIURL}/reports/columns`, {
                params: {
                    base_report: reportName, // reportName is the key here
                    company_id: user.comp_id
                }
            });

            if (response.data && response.data.columns) {
                const columns = response.data.columns;
                setAvailableColumns(columns);
                
                // Initialize all columns as visible by default, unless we're editing and have existing visibility settings
                const initialVisibility = {};
                columns.forEach(col => {
                    // If we have existing config with visibility settings, use them; otherwise default to true
                    if (existingConfig?.columnVisibilityModel && 
                        col.field in existingConfig.columnVisibilityModel) {
                        initialVisibility[col.field] = existingConfig.columnVisibilityModel[col.field];
                    } else {
                        initialVisibility[col.field] = true;
                    }
                });
                setColumnVisibility(initialVisibility);
                
                // Initialize column order - only include visible columns
                if (existingConfig?.columns?.orderedFields) {
                    // Use existing order, filtering to only visible columns
                    const existingOrder = existingConfig.columns.orderedFields.filter(
                        field => initialVisibility[field] !== false
                    );
                    // Add any new visible columns that might not be in the saved order
                    const visibleFields = columns
                        .filter(col => initialVisibility[col.field] !== false)
                        .map(col => col.field);
                    const newFields = visibleFields.filter(field => !existingOrder.includes(field));
                    setColumnOrder([...existingOrder, ...newFields]);
                } else {
                    // Default order based on visible columns only
                    setColumnOrder(
                        columns
                            .filter(col => initialVisibility[col.field] !== false)
                            .map(col => col.field)
                    );
                }
            }
        } catch (err) {
            console.error("Error fetching columns:", err);
            // If the endpoint doesn't exist yet, we'll just skip this feature
            setAvailableColumns([]);
        } finally {
            setIsLoadingColumns(false);
        }
    };

    const handleColumnToggle = (field) => {
        setColumnVisibility(prev => {
            const newVisibility = {
                ...prev,
                [field]: !prev[field]
            };
            
            // Update column order based on visibility change
            setColumnOrder(currentOrder => {
                if (newVisibility[field]) {
                    // Column is being enabled - add to end if not already in order
                    if (!currentOrder.includes(field)) {
                        return [...currentOrder, field];
                    }
                    // If already in order, add to end
                    const filtered = currentOrder.filter(f => f !== field);
                    return [...filtered, field];
                } else {
                    // Column is being disabled - remove from order
                    return currentOrder.filter(f => f !== field);
                }
            });
            
            return newVisibility;
        });
    };

    const handleSelectAllColumns = () => {
        const allVisible = {};
        const allFields = [];
        availableColumns.forEach(col => {
            allVisible[col.field] = true;
            allFields.push(col.field);
        });
        setColumnVisibility(allVisible);
        // Update order to include all columns
        setColumnOrder(allFields);
    };

    const handleDeselectAllColumns = () => {
        const allHidden = {};
        availableColumns.forEach(col => {
            allHidden[col.field] = false;
        });
        setColumnVisibility(allHidden);
        // Clear the order since no columns are visible
        setColumnOrder([]);
    };

    const handleDragStart = (e, index) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === index) return;
        
        const newOrder = [...columnOrder];
        const draggedItem = newOrder[draggedIndex];
        newOrder.splice(draggedIndex, 1);
        newOrder.splice(index, 0, draggedItem);
        
        setColumnOrder(newOrder);
        setDraggedIndex(index);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
    };

    const moveColumnUp = (index) => {
        if (index === 0) return;
        const newOrder = [...columnOrder];
        [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
        setColumnOrder(newOrder);
    };

    const moveColumnDown = (index) => {
        if (index === columnOrder.length - 1) return;
        const newOrder = [...columnOrder];
        [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
        setColumnOrder(newOrder);
    };

    const handleSave = async () => {
        if (!reportName.trim()) {
            setErrorText("Report name is required");
            return;
        }

        if (!baseReport) {
            setErrorText("Please select a base report");
            return;
        }

        try {
            setIsSaving(true);

            // Prepare configuration with column visibility settings
            let configuration = report?.configuration || {};
            // Parse configuration if it's a string
            if (typeof configuration === 'string') {
                try {
                    configuration = JSON.parse(configuration);
                } catch (e) {
                    configuration = {};
                }
            }
            
            // If we have column visibility settings, add them to the configuration
            if (Object.keys(columnVisibility).length > 0) {
                configuration.columnVisibilityModel = columnVisibility;
                // Also add it to columns.columnVisibilityModel for compatibility
                if (!configuration.columns) {
                    configuration.columns = {};
                }
                configuration.columns.columnVisibilityModel = columnVisibility;
            }
            
            // Add column order to configuration
            // Include visible columns in order, followed by hidden columns
            if (columnOrder.length > 0 || Object.keys(columnVisibility).length > 0) {
                if (!configuration.columns) {
                    configuration.columns = {};
                }
                // Get all column fields
                const allFields = availableColumns.map(col => col.field);
                // Get hidden columns (not in current order)
                const hiddenColumns = allFields.filter(field => !columnOrder.includes(field));
                // Combine visible columns (in order) with hidden columns at the end
                configuration.columns.orderedFields = [...columnOrder, ...hiddenColumns];
            }

            const payload = {
                employee_id: user.emp_id,
                company_id: user.comp_id,
                report_name: reportName,
                base_report: baseReport,
                configuration: configuration
            };

            if (report?.id) {
                // Update existing report
                payload.id = report.id;
                await axios.put(`${APIURL}/reports/custom`, payload);
                setSuccessText("Custom report updated successfully");
            } else {
                // Create new report
                const response = await axios.post(`${APIURL}/reports/custom`, payload);
                if (response.data.code === 200) {
                    setSuccessText("Custom report created successfully. Now you can customize the view!");
                } else {
                    throw new Error("Failed to create report");
                }
            }

            onClose(true); // true means refresh the list
        } catch (err) {
            console.error("Error saving custom report:", err);
            setErrorText("Failed to save custom report");
            setIsSaving(false);
        }
    };

    const handleClose = () => {
        if (!isSaving) {
            onClose(false);
        }
    };

    return (
        <Dialog 
            open={open} 
            onClose={handleClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                {report ? "Edit Custom Report" : "Create Custom Report"}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ pt: 2 }}>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                        Create a custom view of an existing data source. You'll be able to customize which columns 
                        to display and their order after saving.
                    </Typography>

                    <TextField
                        fullWidth
                        label="Report Name"
                        value={reportName}
                        onChange={(e) => setReportName(e.target.value)}
                        placeholder="e.g., My Active Customers"
                        sx={{ mb: 2 }}
                        disabled={isSaving}
                        required
                    />

                    <TextField
                        fullWidth
                        select
                        label="Base Data Source"
                        value={baseReport}
                        onChange={(e) => setBaseReport(e.target.value)}
                        sx={{ mb: 2 }}
                        disabled={isSaving || !!report}
                        required
                        helperText={report ? "Base data source cannot be changed after creation" : "Select the data source to customize"}
                    >
                        {availableBaseReports.map((option) => (
                            <MenuItem key={option.key} value={option.key}>
                                {option.name}
                            </MenuItem>
                        ))}
                    </TextField>

                    {/* Column Selection and Ordering Section */}
                    <Collapse in={baseReport && availableColumns.length > 0}>
                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                            {/* Column Visibility */}
                            <Box sx={{ 
                                flex: 1,
                                p: 2, 
                                backgroundColor: "#f9f9f9", 
                                borderRadius: 1,
                                border: "1px solid #e0e0e0"
                            }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <FormLabel component="legend" sx={{ fontWeight: 'bold' }}>
                                        Column Visibility
                                    </FormLabel>
                                    <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                                        <Button 
                                            size="small" 
                                            onClick={handleSelectAllColumns}
                                            disabled={isSaving}
                                            sx={{ whiteSpace: 'nowrap' }}
                                        >
                                            All
                                        </Button>
                                        <Button 
                                            size="small" 
                                            onClick={handleDeselectAllColumns}
                                            disabled={isSaving}
                                            sx={{ whiteSpace: 'nowrap' }}
                                        >
                                            None
                                        </Button>
                                    </Box>
                                </Box>
                                <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
                                    Toggle which columns are visible
                                </Typography>
                                
                                {isLoadingColumns ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                        <CircularProgress size={24} />
                                    </Box>
                                ) : (
                                    <FormGroup sx={{ 
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                                        gap: 1
                                    }}>
                                        {availableColumns.map((col) => (
                                            <FormControlLabel
                                                key={col.field}
                                                control={
                                                    <Checkbox
                                                        checked={columnVisibility[col.field] !== false}
                                                        onChange={() => handleColumnToggle(col.field)}
                                                        disabled={isSaving}
                                                        size="small"
                                                    />
                                                }
                                                label={
                                                    <Typography variant="body2">
                                                        {col.headerName || col.field}
                                                    </Typography>
                                                }
                                            />
                                        ))}
                                    </FormGroup>
                                )}
                            </Box>

                            {/* Column Order */}
                            <Box sx={{ 
                                flex: 1,
                                p: 2, 
                                backgroundColor: "#f9f9f9", 
                                borderRadius: 1,
                                border: "1px solid #e0e0e0"
                            }}>
                                <FormLabel component="legend" sx={{ fontWeight: 'bold', mb: 1, display: 'block' }}>
                                    Column Order
                                </FormLabel>
                                <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
                                    Drag to reorder columns or use arrow buttons
                                </Typography>
                                
                                {isLoadingColumns ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                        <CircularProgress size={24} />
                                    </Box>
                                ) : (
                                    <List sx={{ 
                                        maxHeight: '400px', 
                                        overflowY: 'auto',
                                        p: 0
                                    }}>
                                        {columnOrder.map((fieldName, index) => {
                                            const col = availableColumns.find(c => c.field === fieldName);
                                            if (!col) return null;
                                            
                                            return (
                                                <Paper
                                                    key={fieldName}
                                                    elevation={draggedIndex === index ? 3 : 1}
                                                    draggable
                                                    onDragStart={(e) => handleDragStart(e, index)}
                                                    onDragOver={(e) => handleDragOver(e, index)}
                                                    onDragEnd={handleDragEnd}
                                                    sx={{
                                                        mb: 1,
                                                        p: 1,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        cursor: 'move',
                                                        opacity: draggedIndex === index ? 0.5 : 1,
                                                        backgroundColor: draggedIndex === index ? '#e3f2fd' : 'white',
                                                        '&:hover': {
                                                            backgroundColor: '#f5f5f5'
                                                        }
                                                    }}
                                                >
                                                    <DragIndicatorIcon sx={{ mr: 1, color: 'text.secondary', cursor: 'grab' }} />
                                                    <ListItemText 
                                                        primary={
                                                            <Typography variant="body2">
                                                                {col.headerName || col.field}
                                                            </Typography>
                                                        }
                                                        sx={{ flex: 1 }}
                                                    />
                                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                        <IconButton 
                                                            size="small" 
                                                            onClick={() => moveColumnUp(index)}
                                                            disabled={index === 0 || isSaving}
                                                        >
                                                            <Typography>↑</Typography>
                                                        </IconButton>
                                                        <IconButton 
                                                            size="small" 
                                                            onClick={() => moveColumnDown(index)}
                                                            disabled={index === columnOrder.length - 1 || isSaving}
                                                        >
                                                            <Typography>↓</Typography>
                                                        </IconButton>
                                                    </Box>
                                                </Paper>
                                            );
                                        })}
                                    </List>
                                )}
                            </Box>
                        </Box>
                    </Collapse>

                    {!report && (
                        <Box sx={{ 
                            p: 2, 
                            backgroundColor: "#f5f5f5", 
                            borderRadius: 1,
                            border: "1px solid #e0e0e0"
                        }}>
                            <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                                How it works:
                            </Typography>
                            <Typography variant="body2" component="div">
                                <ol style={{ margin: 0, paddingLeft: 20 }}>
                                    <li>Select a base data source to start from</li>
                                    <li>Give your custom report a unique name</li>
                                    <li>Choose which columns to display and drag to arrange their order</li>
                                    <li>Save and view your customized report</li>
                                </ol>
                            </Typography>
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={handleClose} disabled={isSaving}>
                    Cancel
                </Button>
                <Button 
                    onClick={handleSave} 
                    variant="contained"
                    disabled={isSaving}
                    sx={{ backgroundColor: "#3fadf6" }}
                >
                    {isSaving ? (
                        <>
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                            Saving...
                        </>
                    ) : (
                        report ? "Update Report" : "Create Report"
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
